import axios from 'axios';
import type { CancelTokenSource } from 'axios';

import type { SportsTradingScope } from '../../lib/geocomply/sportsTradingGuard';
import { executeClassifiedTradeRequest } from '../../lib/geocomply/tradeRequestGateway';
import type { ServerError, TradeVo } from '../../models';
import type { OrderVo } from '../../models/order';
import type {
    AmendOrderRequest,
    AmendOrderResponse,
    CancelOrderRequest,
    CancelOrderResponse,
    DecreaseOrderRequest,
    DecreaseOrderResponse,
    GetMarketOrdersParams,
    GetMarketOrdersResponse,
    GetOrderQueuePositionRequest,
    GetOrderQueuePositionResponse,
    GetTradesResponse,
    GetUserTradesParams,
    SubmitOrderRequest,
    SubmitOrderResponse,
} from '../../models/reqres';
import { buildQueryString } from '../../utils/query';
import { exchangeApi, handleErrorCallback, noop, rateLimiter } from '../core';

export function submitOrder(
    userId: string,
    order: SubmitOrderRequest,
    onSuc: (order: OrderVo, messageCode: string) => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
    sportsTradingScope: SportsTradingScope,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    executeClassifiedTradeRequest<SubmitOrderResponse>(exchangeApi, {
        method: 'post',
        url: `/users/${userId}/orders`,
        data: order,
        sportsTradingScope,
        config: { cancelToken: cancelRef.token },
    })
        .then((response) => {
            onSuc(response.data.order, response.data.status);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function decreaseOrder(
    userId: string,
    payload: DecreaseOrderRequest,
    onSuc: (response: DecreaseOrderResponse) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
    sportsTradingScope: SportsTradingScope,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    executeClassifiedTradeRequest<DecreaseOrderResponse>(exchangeApi, {
        method: 'post',
        url: `/users/${userId}/orders/${payload.order_id}/decrease`,
        data: { count: payload.count },
        sportsTradingScope,
        config: { cancelToken: cancelRef.token },
    })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function amendOrder(
    userId: string,
    orderId: string,
    payload: AmendOrderRequest,
    sportsTradingScope: SportsTradingScope,
    onSuc: (response: DecreaseOrderResponse) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    executeClassifiedTradeRequest<AmendOrderResponse>(exchangeApi, {
        method: 'post',
        url: `/users/${userId}/orders/${orderId}/amend`,
        data: payload,
        sportsTradingScope,
        config: { cancelToken: cancelRef.token },
    })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function cancelOrder(
    userId: string,
    payload: CancelOrderRequest,
    onSuc: () => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .delete<CancelOrderResponse>(`/users/${userId}/orders/${payload.order_id}`, {
            cancelToken: cancelRef.token,
            params: { market_ticker: payload.market_ticker },
        })
        .then(onSuc)
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getOrderQueuePosition(
    userId: string,
    payload: GetOrderQueuePositionRequest,
    onSuc: (queuePosition: number) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetOrderQueuePositionResponse>(`/users/${userId}/orders/${payload.order_id}/queue_position`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data.queue_position);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getOrders(
    userId: string,
    onSuc: (orders: Array<OrderVo>, cursor?: string) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
    params?: GetMarketOrdersParams,
    onLog?: (parsedError: ServerError) => void,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();
    // Normalize: always opt into cursor pagination (API requires cursor= on first request)
    const cursorParams = params ? { ...params, cursor: params.cursor ?? '' } : { cursor: '' };
    let query = buildQueryString(cursorParams);
    if (cursorParams.cursor === '') {
        query = query ? `${query}&cursor=` : '?cursor=';
    }
    const ordersUrl = `/users/${userId}/orders${query}`;

    rateLimiter
        .getLimiter('getOrders')
        .schedule(() => exchangeApi.get<GetMarketOrdersResponse>(ordersUrl, { cancelToken: cancelRef.token }))
        .then((response) => {
            onSuc(response.data.orders || [], response.data.cursor ?? undefined);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr, onLog);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getTrades(
    userId: string,
    onSuc: (trades: Array<TradeVo>, cursor?: string) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
    params?: GetUserTradesParams,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetTradesResponse>(`/users/${userId}/trades`, {
            cancelToken: cancelRef.token,
            params: params,
        })
        .then((response) => {
            onSuc(response.data.trades || [], response.data.cursor ?? undefined);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}
